import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  TableCell,
  TableRow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { StatusChip, triMeta } from "../../components/ui";
import { monoFontFamily } from "../../theme/tokens";
import type { CSITipaltiStaging } from "../../types/CSITypes";
import { PayeeIdsDialog } from "./Modals";
import { RecordDetails, RecordLinkHandlers } from "./RecordDetails";
import { ReviewForm } from "./ReviewForm";
import { reviewResultMeta } from "./reviewResults";
import { SectionCaption, TipaltiCompare } from "./TipaltiCompare";

/** Number of table columns, for the expanded row's colSpan. */
export const STAGING_COLUMN_COUNT = 9;

export interface StagingRowProps extends RecordLinkHandlers {
  row: CSITipaltiStaging;
  selected: boolean;
  expanded: boolean;
  onToggleSelected: (row: CSITipaltiStaging) => void;
  onToggleExpanded: (id: string) => void;
  /** Receives the row as it reads after a review save. */
  onSaved: (row: CSITipaltiStaging) => void;
  /** Admin only: whether the tax ID can be shown in full. */
  canRevealTaxId: boolean;
  /** True while a bulk update runs. */
  selectionDisabled?: boolean;
}

/**
 * One analysis row: selection, payee, In Tipalti / Matched / Payable / Active, Pays by,
 * Review and Open / Close. Opening it shows the CSI Pay / Tipalti compare, the record
 * details (every identifier and link) and the review form.
 */
export function StagingRow({
  row,
  selected,
  expanded,
  onToggleSelected,
  onToggleExpanded,
  onSaved,
  canRevealTaxId,
  selectionDisabled,
  onOpenPayeeDetail,
  onOpenTaxIdMatches,
  onOpenMatchedPayee,
}: StagingRowProps) {
  const [payeeIdsOpen, setPayeeIdsOpen] = useState(false);
  const panelId = `staging-row-${row.id}-details`;
  const name = row.csiName || "Unnamed payee";
  const groups = row.groupPayeeIds.length;

  return (
    <>
      <TableRow
        hover
        selected={selected}
        onClick={() => {
          // Selecting text (e.g. to copy a payee ID) should not open or close the row.
          if (window.getSelection()?.toString()) return;
          onToggleExpanded(row.id);
        }}
        sx={{
          cursor: "pointer",
          "& > td": expanded ? { borderBottom: "none" } : undefined,
        }}
      >
        <TableCell
          padding="checkbox"
          onClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            disabled={selectionDisabled}
            onChange={() => onToggleSelected(row)}
            inputProps={{ "aria-label": `Select ${name}` }}
          />
        </TableCell>
        <TableCell sx={{ maxWidth: 320 }}>
          <Box
            sx={{
              fontWeight: 500,
              color: row.csiName ? "text.primary" : "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.csiName || undefined}
          >
            {name}
          </Box>
          <Box
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Box component="span" sx={{ fontFamily: monoFontFamily }}>
              {row.payeeId || "No payee ID"}
            </Box>
            {" · "}
            <Box
              component="span"
              sx={(theme) =>
                groups > 1
                  ? { color: theme.palette.status.warn.fg, fontWeight: 500 }
                  : {}
              }
            >
              group {groups}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <StatusChip size="small" meta={triMeta(row.inTipalti)} />
        </TableCell>
        <TableCell>
          <StatusChip size="small" meta={triMeta(row.isMatchedWithTipalti)} />
        </TableCell>
        <TableCell>
          <StatusChip size="small" meta={triMeta(row.isPayable)} />
        </TableCell>
        <TableCell>
          <StatusChip size="small" meta={triMeta(row.isActive)} />
        </TableCell>
        <TableCell>
          <StatusChip
            size="small"
            tone="neutral"
            label={row.csiPaymentMethod || "Not set"}
          />
        </TableCell>
        <TableCell>
          <StatusChip
            size="small"
            meta={reviewResultMeta(row.reviewedResults)}
          />
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="outlined"
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={`${expanded ? "Close" : "Open"} ${name}`}
            endIcon={
              expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
            }
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded(row.id);
            }}
          >
            {expanded ? "Close" : "Open"}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={STAGING_COLUMN_COUNT}
          sx={{ p: 0, ...(expanded ? {} : { borderBottom: "none" }) }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              id={panelId}
              role="region"
              aria-label={`Details for ${name}`}
              sx={{
                bgcolor: "grey.50",
                px: 4,
                py: 4,
                display: "grid",
                gap: 5,
              }}
            >
              <TipaltiCompare row={row} />
              <RecordDetails
                row={row}
                canRevealTaxId={canRevealTaxId}
                onOpenPayeeDetail={onOpenPayeeDetail}
                onOpenTaxIdMatches={onOpenTaxIdMatches}
                onOpenMatchedPayee={onOpenMatchedPayee}
                onShowPayeeIds={() => setPayeeIdsOpen(true)}
              />
              <Box>
                <SectionCaption>Review</SectionCaption>
                <ReviewForm row={row} onSaved={onSaved} />
              </Box>
            </Box>
          </Collapse>
          <PayeeIdsDialog
            row={row}
            open={payeeIdsOpen}
            onClose={() => setPayeeIdsOpen(false)}
            canRevealTaxId={canRevealTaxId}
            onOpenPayeeDetail={onOpenPayeeDetail}
            onOpenTaxIdMatches={onOpenTaxIdMatches}
            onOpenMatchedPayee={onOpenMatchedPayee}
          />
        </TableCell>
      </TableRow>
    </>
  );
}

export default React.memo(StagingRow);
